import React from "react";
import RosterRow from "./RosterRow";
import { CATEGORIES, REINFORCEMENT_CATEGORY } from "../constants";
import { findUnit, lineTotal, lineBrPerCopy, countRestricted, restrictedLimit, isAtMax, ordersFormula, scoutingTotal } from "../lib/army";

export default function Roster({ faction, roster, totals, limit, changeQty, removeUnit, moveUnit, toggleGroupItem, showReinforcements, toggleReinforcements }) {
  const REQUIRED_CATEGORIES = ["Forward Screen Units", "Vanguard Unit"];
  const restrictedCount = countRestricted(faction, roster);
  const restrictedMax = restrictedLimit(limit);
  const restrictedOver = faction && restrictedCount > restrictedMax;
  const pointsOver = faction && limit > 0 && totals.pts > limit;
  const orders = faction ? ordersFormula(faction, roster, limit) : "";
  const scouting = faction ? scoutingTotal(faction, roster) : 0;
  const visibleCategories = CATEGORIES.filter(
    (cat) => cat !== REINFORCEMENT_CATEGORY || showReinforcements
  );
  return (
    <section className="col-roster" aria-label="Army roster">
      <div className="roster-header roster-header-bound">
        <div className="roster-header-left">
          <div className="roster-title">Battlegroup roster</div>
          <div className="roster-sub" data-testid="roster-subline">
            {faction ? `${faction.name} \u00b7 ${faction.group || ""}` : "No army loaded"}
          </div>
        </div>
        {faction && (
          <div className="roster-header-right">
            <div className="roster-orders" data-testid="roster-orders">
              <div className="orders-title">ORDERS</div>
              <div className="orders-formula" data-testid="orders-formula">{orders}</div>
              <div className="orders-scouting" data-testid="orders-scouting">
                Scouting: <span className="numcell">{scouting}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {restrictedOver && (
        <div className="restricted-warning" data-testid="restricted-warning">
          ⚠ Restricted units: {restrictedCount} selected — maximum {restrictedMax} allowed at {limit} pts
        </div>
      )}

      {pointsOver && (
        <div className="restricted-warning" data-testid="points-warning">
          ⚠ Points total: {totals.pts} exceeds the {limit} pt limit for this army size
        </div>
      )}

      <div data-testid="roster-categories">
        {faction &&
          visibleCategories.map((cat) => {
            const entries = roster.filter((e) => {
              const u = findUnit(faction, e.unitId);
              return u && u.category === cat;
            });
            const subtotal = entries.reduce((sum, e) => sum + lineTotal(faction, e), 0);
            const subtotalBr = entries.reduce((sum, e) => {
              const u = findUnit(faction, e.unitId);
              return sum + (u ? lineBrPerCopy(u, e) * e.qty : 0);
            }, 0);

            return (
              <div className="category-block" key={cat}>
                <div className="category-header">
                  <h3>{cat === REINFORCEMENT_CATEGORY ? "Reinforcement Requests" : cat}</h3>
                  <div className="cat-totals">
                    <div className="cat-subtotal" data-testid={`cat-subtotal-pts-${cat}`}><span className="numcell">{subtotal}</span> pts</div>
                    <div className="cat-subtotal cat-subtotal-br" data-testid={`cat-subtotal-br-${cat}`}>BR: <span className="numcell">{subtotalBr}</span></div>
                  </div>
                </div>
                <div className="category-body">
                  {entries.length === 0 ? (
                    REQUIRED_CATEGORIES.includes(cat) ? (
                      <div className="section-required-warning" data-testid={`section-warning-${cat}`}>
                        ⚠ A minimum of one unit is required in {cat}
                      </div>
                    ) : (
                      <div className="roster-empty">No units added.</div>
                    )
                  ) : (
                    entries.map((entry, idx) => {
                      const u = findUnit(faction, entry.unitId);
                      const siblings = roster.filter((e) => e.unitId === entry.unitId);
                      const unitTotal = siblings.reduce((s, e) => s + e.qty, 0);
                      return (
                        <RosterRow
                          key={entry.instanceId}
                          faction={faction}
                          entry={entry}
                          changeQty={changeQty}
                          removeUnit={removeUnit}
                          moveUnit={moveUnit}
                          canMoveUp={idx > 0}
                          canMoveDown={idx < entries.length - 1}
                          toggleGroupItem={toggleGroupItem}
                          unitAtMax={isAtMax(u, unitTotal)}
                          siblings={siblings}
                        />
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}

        {faction && (
          <div className="roster-total-bar">
            <label className="reinf-toggle">
              <input
                type="checkbox"
                checked={showReinforcements}
                onChange={(e) => toggleReinforcements(e.target.checked)}
                data-testid="reinforcements-toggle"
              />
              Optional Reinforcement Requests
            </label>
            <div className="restricted-count" data-testid="restricted-count">
              Restricted Units: <span className="numcell">{restrictedCount}</span> / {restrictedMax === Infinity ? "\u221e" : restrictedMax}
            </div>
            <div>Total <b data-testid="total-pts"><span className="numcell-wide">{totals.pts}</span> pts</b></div>
            <div>BR: <b className="br" data-testid="total-br"><span className="numcell">{totals.br}</span></b></div>
          </div>
        )}
      </div>
    </section>
  );
}
